// Working hypotheses (D451, D452): a report keeps the analyst's list of hypotheses, each with its support, its limits
// and the public-data test that would settle it, linked to the claims it rests on (claims of the report's findings,
// cited by ref). A "scorecard" section of the report summarises them in the synthesis: its text is drafted from the
// linked claims, steered by the hypotheses themselves; its table lists them with their status and the strength of
// the facts behind each one.

import type { DossierTable } from './dossier';
import type { ReliabilityStatus } from './reliability';
import { RELIABILITY_LABELS } from './reliability';
import type { ReportLanguage } from './report-language';
import type { ReportTemplateSection } from './reports';

export const HYPOTHESIS_STATUSES = ['open', 'supported', 'weakened', 'refuted'] as const;
export type HypothesisStatus = (typeof HYPOTHESIS_STATUSES)[number];
export const isHypothesisStatus = (v: unknown): v is HypothesisStatus => HYPOTHESIS_STATUSES.includes(v as HypothesisStatus);

export const HYPOTHESES_LIMITS = {
  perReport: 50,
  title: 200,
  /** Characters of the statement, the support, the limits and the test, each. */
  text: 2000,
  claims: 100,
} as const;

export interface Hypothesis {
  id: string;
  reportId: string;
  position: number;
  title: string;
  /** The hypothesis in one or two sentences. */
  statement: string;
  /** What supports it (words; the claims are `claimIds`). */
  support: string;
  /** What limits it: single sources, gaps, contradictions. */
  limits: string;
  /** The public-data test that would settle it: a register, a filing, a record to check. */
  test: string;
  status: HypothesisStatus;
  /** The claims it rests on (current, on-record claims of the report). */
  claimIds: string[];
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** What the facts behind a hypothesis amount to, read when shown or exported. */
export interface HypothesisFacts {
  count: number;
  /** The strongest reliability status among them, or null without claims. */
  strongest: ReliabilityStatus | null;
  byStatus: Record<ReliabilityStatus, number>;
}

export type HypothesisInput = Pick<Hypothesis, 'title' | 'statement' | 'support' | 'limits' | 'test' | 'status' | 'claimIds'>;

const CLAIM_ID = /^[0-9A-Za-z_-]{1,64}$/;

/**
 * A hypothesis as posted: the title is required (on create), the texts are trimmed and limited, the status must be
 * one of ours, the claim ids well formed and unique (their existence is checked against the claims service).
 * `partial` keeps absent fields absent (a PATCH).
 */
export function parseHypothesisInput(
  raw: unknown,
  opts: { partial?: boolean } = {},
): { ok: true; value: Partial<HypothesisInput> } | { ok: false; error: string } {
  const b = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: Partial<HypothesisInput> = {};
  const text = (key: 'statement' | 'support' | 'limits' | 'test', label: string) => {
    if (b[key] === undefined || b[key] === null) return null;
    if (typeof b[key] !== 'string') return `${label} must be text.`;
    const v = (b[key] as string).replace(/\r\n?/g, '\n').trim();
    if (v.length > HYPOTHESES_LIMITS.text)
      return `Keep ${label.toLowerCase()} under ${HYPOTHESES_LIMITS.text.toLocaleString('en')} characters.`;
    out[key] = v;
    return null;
  };
  if (b.title !== undefined || !opts.partial) {
    const title = typeof b.title === 'string' ? b.title.replace(/\s+/g, ' ').trim() : '';
    if (!title) return { ok: false, error: 'Give the hypothesis a title.' };
    if (title.length > HYPOTHESES_LIMITS.title) return { ok: false, error: `Keep the title under ${HYPOTHESES_LIMITS.title} characters.` };
    out.title = title;
  }
  for (const [key, label] of [
    ['statement', 'The statement'],
    ['support', 'The support'],
    ['limits', 'The limits'],
    ['test', 'The test'],
  ] as const) {
    const err = text(key, label);
    if (err) return { ok: false, error: err };
    if (!opts.partial && out[key] === undefined) out[key] = '';
  }
  if (b.status !== undefined) {
    if (!isHypothesisStatus(b.status)) return { ok: false, error: 'The status is open, supported, weakened or refuted.' };
    out.status = b.status;
  } else if (!opts.partial) out.status = 'open';
  if (b.claimIds !== undefined) {
    if (!Array.isArray(b.claimIds) || !b.claimIds.every(x => typeof x === 'string' && CLAIM_ID.test(x)))
      return { ok: false, error: 'The claims must be a list of claim ids.' };
    const ids = [...new Set(b.claimIds as string[])];
    if (ids.length > HYPOTHESES_LIMITS.claims)
      return { ok: false, error: `Link at most ${HYPOTHESES_LIMITS.claims} claims to one hypothesis.` };
    out.claimIds = ids;
  } else if (!opts.partial) out.claimIds = [];
  return { ok: true, value: out };
}

// ─── The scorecard section ───────────────────────────────────────────────────

export const HYPOTHESES_SECTION_KEY = 'hypotheses';

export interface HypothesesLabels {
  sectionTitle: string;
  tableTitle: string;
  headers: [string, string, string, string, string, string, string];
  status: Record<HypothesisStatus, string>;
  /** "4 facts · Corroborated". */
  facts: (f: HypothesisFacts) => string;
  none: string;
}

const factsWords = (lang: ReportLanguage) => (f: HypothesisFacts) => {
  const n = f.count;
  const facts = lang === 'fr' ? `${n} ${n === 1 ? 'fait' : 'faits'}` : `${n} ${n === 1 ? 'fact' : 'facts'}`;
  return f.strongest ? `${facts} · ${RELIABILITY_LABELS[lang][f.strongest]}` : facts;
};

export const HYPOTHESES_LABELS: Record<ReportLanguage, HypothesesLabels> = {
  en: {
    sectionTitle: 'Working hypotheses',
    tableTitle: 'Scorecard of the hypotheses',
    headers: ['#', 'Hypothesis', 'Support', 'Limits', 'Public-data test', 'Status', 'Facts'],
    status: { open: 'Open', supported: 'Supported', weakened: 'Weakened', refuted: 'Refuted' },
    facts: factsWords('en'),
    none: 'No working hypothesis yet.',
  },
  fr: {
    sectionTitle: 'Hypothèses de travail',
    tableTitle: 'Tableau de bord des hypothèses',
    headers: ['N°', 'Hypothèse', 'Appui', 'Limites', 'Test par les données publiques', 'Statut', 'Faits'],
    status: { open: 'Ouverte', supported: 'Étayée', weakened: 'Affaiblie', refuted: 'Réfutée' },
    facts: factsWords('fr'),
    none: 'Aucune hypothèse de travail pour l’instant.',
  },
};

export const hypothesesLabels = (lang: ReportLanguage = 'en'): HypothesesLabels => HYPOTHESES_LABELS[lang];

/** The guidance the scorecard section is drafted with (the hypotheses themselves are sent as the analyst's notes). */
export const HYPOTHESES_SECTION_GUIDANCE =
  'The scorecard of the report’s working hypotheses: for each one, what the facts support, what limits it, and the public-data test that would settle it; say which hypotheses stand, which are weakened and which are refuted, citing the claims. Keep the order of the hypotheses.';

/** The scorecard section, titled in the report's language. */
export const hypothesesSection = (lang: ReportLanguage = 'en'): ReportTemplateSection => ({
  key: HYPOTHESES_SECTION_KEY,
  title: hypothesesLabels(lang).sectionTitle,
  guidance: HYPOTHESES_SECTION_GUIDANCE,
  starter: null,
  kind: 'hypotheses',
});

export const isHypothesesSection = (s: Partial<ReportTemplateSection> | null | undefined): boolean => s?.kind === 'hypotheses';

/**
 * The report's sections with the scorecard added: before "recommendations" (the analyst's own chapter), else last,
 * so it follows the case files and the patterns. Left as it is when already there.
 */
export function insertHypothesesSection(
  sections: readonly ReportTemplateSection[],
  section: ReportTemplateSection,
): ReportTemplateSection[] {
  if (sections.some(s => s.key === section.key)) return [...sections];
  const out = [...sections];
  const at = out.findIndex(s => s.key === 'recommendations');
  out.splice(at < 0 ? out.length : at, 0, section);
  return out;
}

/** "1. Title — statement (Supported). Support: … Limits: … Test: …", the notes a scorecard draft is steered with. */
export function hypothesesNotes(list: readonly Hypothesis[], lang: ReportLanguage = 'en'): string {
  const l = hypothesesLabels(lang);
  const lines = list.map((h, i) =>
    [
      `${i + 1}. ${h.title}${h.statement ? ` — ${h.statement}` : ''} (${l.status[h.status]})`,
      h.support ? `Support: ${h.support}` : '',
      h.limits ? `Limits: ${h.limits}` : '',
      h.test ? `Test: ${h.test}` : '',
    ]
      .filter(Boolean)
      .join(' '),
  );
  return `The analyst's working hypotheses, to summarise in this order:\n${lines.join('\n')}`;
}

/** The scorecard as a table of the Word/PDF export, in the document's language. */
export function hypothesesTable(
  list: readonly Hypothesis[],
  facts: ReadonlyMap<string, HypothesisFacts>,
  lang: ReportLanguage = 'en',
): DossierTable {
  const l = hypothesesLabels(lang);
  return {
    title: l.tableTitle,
    headers: [...l.headers],
    rows: list.map((h, i) => [
      String(i + 1),
      h.statement ? `${h.title}. ${h.statement}` : h.title,
      h.support,
      h.limits,
      h.test,
      l.status[h.status],
      l.facts(
        facts.get(h.id) ?? {
          count: h.claimIds.length,
          strongest: null,
          byStatus: { official_document: 0, corroborated: 0, single_source: 0, hypothesis: 0 },
        },
      ),
    ]),
    total: list.length,
    note: null,
  };
}
